// ⚠️ NO RELATIVE IMPORTS — a bundler compiles this from source; web's protocol-imports.leaf.test.ts enforces it.
// App-level message shortcut for session controls, shared by Socket Mode and HTTP apps.
export const SLACK_MANAGE_SESSION_SHORTCUT_CALLBACK_ID = 'ac_manage_session';
/** Public platform profile copy. Never derive this from an Agent description. */
export const PLATFORM_APP_DESCRIPTION = 'AI agent powered by AgentConnect.';
// Request only scopes used by implemented features; the same list validates installation grants.
export const SLACK_BOT_SCOPES = [
    'files:read',
    'app_mentions:read',
    'channels:history',
    'channels:read',
    'commands',
    'chat:write',
    'chat:write.customize',
    'files:write',
    'groups:history',
    'groups:read',
    'im:history',
    // DM channel lookup needs `im:read` to keep inbound and outbound session keys consistent.
    'im:read',
    'im:write',
    'mpim:history',
    'mpim:read',
    'reactions:read',
    'reactions:write',
    'assistant:write',
    'users:read',
    'canvases:read',
    'canvases:write',
    // `conversations.leave` has always needed one of these two; the manifest never asked.
    'channels:manage',
    'groups:write',
    'mpim:write',
    'bookmarks:read',
    'bookmarks:write',
    'lists:read',
    'lists:write',
    // Join public channels on first read or post; private channels still require an invitation.
    'channels:join',
    // The search tool requests public-channel messages and returns message results only.
    'search:read.public'
];
// Socket Mode and relay HTTP ingress deliver the same events to the owning daemon.
export const SLACK_BOT_EVENTS = [
    // The native stop button on an agent session; without the subscription Slack never shows it.
    'agent_session_stopped',
    // Subscribed but not handled yet, so title sync can land without a second manifest refresh.
    'agent_session_title_changed',
    'app_mention',
    'app_home_opened',
    'app_uninstalled',
    'assistant_thread_started',
    // Same: the shipping context signal for an assistant thread, pre-provisioned and inert today.
    'assistant_thread_context_changed',
    'message.channels',
    'message.groups',
    'message.im',
    'message.mpim',
    'member_joined_channel',
    'channel_left',
    'group_left',
    'tokens_revoked'
];
export const DEFAULT_SLACK_APP_NAME = 'agentconnect';
export function slackEventsRequestUrl(relayUrl) {
    return `${normalizeSlackRelayUrl(relayUrl)}/slack/events`;
}
export function slackInteractionsRequestUrl(relayUrl) {
    return `${normalizeSlackRelayUrl(relayUrl)}/slack/interactions`;
}
function normalizeSlackRelayUrl(relayUrl) {
    return relayUrl.replace(/^ws(s?):\/\//i, 'http$1://').replace(/\/+$/, '');
}
/** One canonical Slack manifest shared by browser, Control Plane, and Setup Server. */
export function buildSlackAppManifest(name, options = {}) {
    const appName = name.trim() || DEFAULT_SLACK_APP_NAME;
    const displayName = options.displayName?.trim() || appName;
    const relayUrl = options.relayUrl ? normalizeSlackRelayUrl(options.relayUrl) : undefined;
    const http = Boolean(relayUrl);
    const redirectUrls = [...new Set(options.redirectUrls ?? [])];
    return {
        display_information: {
            name: appName,
            ...(options.backgroundColor ? { background_color: options.backgroundColor } : {})
        },
        features: {
            bot_user: { display_name: displayName, always_online: true },
            app_home: { home_tab_enabled: true, messages_tab_enabled: true, messages_tab_read_only_enabled: false },
            agent_view: {
                agent_description: PLATFORM_APP_DESCRIPTION,
                suggested_prompts: []
            },
            shortcuts: [
                {
                    name: 'Manage session',
                    type: 'message',
                    callback_id: SLACK_MANAGE_SESSION_SHORTCUT_CALLBACK_ID,
                    description: 'View or update the AgentConnect session for this conversation'
                }
            ]
        },
        oauth_config: {
            scopes: { bot: [...SLACK_BOT_SCOPES] },
            ...(redirectUrls.length > 0 ? { redirect_urls: redirectUrls } : {}),
            ...(options.pkceEnabled !== undefined ? { pkce_enabled: options.pkceEnabled } : {})
        },
        settings: {
            event_subscriptions: {
                bot_events: [...SLACK_BOT_EVENTS],
                ...(relayUrl ? { request_url: slackEventsRequestUrl(relayUrl) } : {})
            },
            interactivity: {
                is_enabled: true,
                ...(relayUrl
                    ? {
                        request_url: slackInteractionsRequestUrl(relayUrl),
                        message_menu_options_url: slackInteractionsRequestUrl(relayUrl)
                    }
                    : {})
            },
            org_deploy_enabled: false,
            socket_mode_enabled: !http,
            token_rotation_enabled: false,
            is_mcp_enabled: false
        }
    };
}
//# sourceMappingURL=slack-app-manifest.js.map